/* eslint-disable react/no-children-prop */
import {
  InputAdornment,
  TablePagination,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import {
  Loader,
  PrimarySelect,
  PrimaryTextField,
  StickyHeader,
} from 'components';
import { SearchIcon } from 'assets/icons';
import { useDispatch, useSelector } from 'react-redux';
import { reviewAction } from 'pages/review/components/review-list/components/slice';
import { selectReviews, selectReviewsLoader, selectReviewsTotal } from 'pages/review/components/review-list/components/selector';
import Review from './components';

const ReviewList = () => {
  const dispatch = useDispatch();
  const sortByOptions = [{ id: 1, value: 'date-asc', text: 'Date ascending' }, { id: 2, value: 'date-desc', text: 'Date descending' }, { id: 3, value: 'rate-asc', text: 'Rating ascending' }, { id: 4, value: 'rate-desc', text: 'Rating descending' }];

  const [keyword, setKeyword] = React.useState<string>('');
  const [selectedSortBy, setSelectedSortBy] = React.useState<string>(sortByOptions[1].value);

  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(9);
  const reviews = useSelector(selectReviews);
  const loading = useSelector(selectReviewsLoader);
  const total = useSelector(selectReviewsTotal);

  useEffect(() => {
    dispatch(reviewAction.getReviews({
      keyword,
      page: (page + 1),
      limit: rowsPerPage,
      sortBy: selectedSortBy as string,
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [dispatch, selectedSortBy, keyword, page, rowsPerPage]);

  const handleSearchChange = (event: any) => {
    setKeyword(event.target.value);
  };

  const handleSortByChange = (event: any) => {
    setSelectedSortBy(event.target.value);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <StickyHeader>
        <div data-testid="review-list-info" className="row mb-2">
          <div className=" col-lg-3 col-xl-2 mb-3 mb-md-0 d-flex flex-column justify-content-center">
            <Typography variant="h4" sx={{ mb: 0.5 }}>
              Reviews
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {!loading && `You have ${total} reviews.`}
            </Typography>
          </div>
          <div className="col-lg-9 col-xl-10">
            <div className="row justify-content-lg-end gy-2 align-items-center gx-3 gx-lg-4">
              <div className="col-sm-4 col-lg-4 col-xl-3 mb-2 mb-md-0">
                <PrimaryTextField
                  placeholder="Search"
                  endAdornment={(
                    <InputAdornment aria-label="search-icon" position="end">
                      <SearchIcon />
                    </InputAdornment>
                  )}
                  value={keyword}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="col-sm-4 col-lg-4 col-xl-3 mb-2 mb-md-0">
                <PrimarySelect
                  inlineLabel
                  label="Sort"
                  options={sortByOptions}
                  onChange={handleSortByChange}
                  value={selectedSortBy}
                />
              </div>
            </div>
          </div>
        </div>
      </StickyHeader>
      <Loader loading={loading ?? false}>
        <div data-testid="review-list-reviews" className="row">
          {reviews.map((review) => (
            <Review key={review._id} review={review} />
          ))}
        </div>
        <div className="row my-3">
          <div className="col-12 d-flex flex-column align-items-center justify-content-center">
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPageOptions={[9, 18]}
              labelRowsPerPage="Items per page"
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </div>
      </Loader>
    </>
  );
};

export default ReviewList;
